// © Microsoft Corporation. All rights reserved.

///<reference path="../types/lru-cache-for-clusters-as-promised.d.ts" />

import { MockLRUCache } from "./ServerCacheMock";
import { canUseDOM } from "@msnews/isomorphic-util";

/**
 * Clustered cache.
 * @type {LRUCache}
 */
let serverCache: LRUCache<string, any>;

/**
 * Initializes the serverCache with the LRUCache instance provided by the Render Service.
 * For testing it is initialized with a mock instance.
 * This needs to be called once on the server side.
 * @param cache
 */
function initialize(cache: LRUCache<string, any>): void {
    serverCache = cache;
}

/**
 * Reset the serverCache, clearing all data from the cache.
 * Currently only used for unit tests.
 * @returns {ServerCache}
 */
async function reset(): Promise<void> {
    await serverCache.reset();
}

/**
 * Set cache. Update the "recently used"-ness of the key.
 * @param key Cache key to set.
 * @param value Cache value to set.
 * @param maxAge Optional maximum age in milliseconds.
 */
async function set(key: string, value: any, maxAge?: number): Promise<void> {
    await serverCache.set(key, value, maxAge);
}

/**
 * Get cache. Update the "recently used"-ness of the key.
 * @param key Cache key to get.
 * @returns Cached object. If not found, return undefined.
 */
async function get(key: string): Promise<any> {
    return await serverCache.get(key);
}

/**
 * Check if a key is in the cache, without updating the recent-ness or deleting it for being stale.
 * @param key Cache key to check.
 * @returns True if key exists.
 */
async function has(key: string): Promise<boolean> {
    return await serverCache.has(key);
}

/**
 * Removes a value from the cache.
 * @param key Cache key to delete.
 * @returns Promise for completing the task
 */
async function del(key: string): Promise<void> {
    return await serverCache.del(key);
}

/**
 * Return total quantity of objects currently in cache. Note, that stale (see options) items are returned as part of this item count.
 * @returns Total quantity of objects currently in cache.
 */
async function itemCount(): Promise<number> {
    return await serverCache.itemCount();
}

/**
 * ServerCache is server side only data cache.
 * More detailed docs are in the following links
 * https://github.com/isaacs/node-lru-cache#readme
 * https://github.com/doublesharp/lru-cache-for-clusters-as-promised
 */
export const ServerCache = (!canUseDOM()) ? {
    initialize,
    reset,
    set,
    get,
    has,
    del,
    itemCount
} : null;

// Initialize ServerCache using mock cache for tests, we do it here so it works for relative imports
if (global["TEST_ENV"] && !canUseDOM()) {
    // MockLRUCache implements only a subset of LRUCache; it implements only the functions currently being used.
    // This was simply to avoid exhaustively reimplementing the whole API.
    // If more functions are utilized from the API a mock implmenting will need provided for in the MockServerCache class.
    // @ts-ignore
    ServerCache.initialize(new MockLRUCache());
}
